import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { getCharacterById } from "@/mocks/characters";
import { getWorkById } from "@/mocks/works";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ShareButtons from "@/components/feature/ShareButtons";

export default function CharacterDetail() {
  const { id } = useParams<{ id: string }>();
  const character = id ? getCharacterById(id) : undefined;

  useSeo({
    title: character ? `${character.name} - 希岛爱理角色解析` : "角色未找到 - 希岛爱理角色解析",
    description: character
      ? `${character.name}（出自《${character.workTitle}》）角色解析：${character.description}`
      : "未找到对应的角色解析。",
    keywords: character
      ? `${character.name},${character.workTitle},角色解析,希岛爱理角色`
      : "希岛爱理角色解析",
  });

  if (!character) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <i className="ri-error-warning-line text-4xl text-foreground-300" />
        <h1 className="font-heading text-xl font-bold text-foreground-950 mt-4">未找到该角色</h1>
        <p className="text-sm text-foreground-500 mt-2">角色可能已被移除或地址有误。</p>
        <Link
          to="/characters"
          className="mt-6 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          返回角色专题
        </Link>
      </div>
    );
  }

  const work = getWorkById(character.workId);

  return (
    <div className="w-full pb-16">
      <Breadcrumbs
        items={[
          { label: "首页", to: "/" },
          { label: "角色解析", to: "/characters" },
          { label: character.name },
        ]}
      />

      <div className="w-full px-4 md:px-8 pt-6">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
          <div className="w-full lg:w-[340px] shrink-0">
            <div className="w-full aspect-[4/5] rounded-lg overflow-hidden bg-background-200 border border-background-200">
              <img
                src={character.image}
                alt={`${character.name} 角色形象`}
                title={`${character.name} - 希岛爱理角色解析`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1">
            <span className="text-xs font-label font-semibold tracking-[0.2em] uppercase text-accent-600">
              Character
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-2">
              {character.name}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              所属作品 ·{" "}
              {work ? (
                <Link
                  to={`/works/${work.id}`}
                  className="text-primary-700 hover:text-accent-600 transition-colors font-medium"
                >
                  《{character.workTitle}》
                </Link>
              ) : (
                `《${character.workTitle}》`
              )}
            </p>

            <div className="mt-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-2">角色简介</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                {character.description}
              </p>
            </div>

            <blockquote className="mt-6 rounded-lg bg-primary-950 p-6 text-background-100">
              <i className="ri-double-quotes-l text-2xl text-accent-400" />
              <p className="font-heading text-lg md:text-xl text-background-50 mt-2 leading-relaxed">
                {character.quote}
              </p>
              <footer className="text-xs text-background-400 mt-3">— 代表台词</footer>
            </blockquote>

            <div className="mt-8 w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200 border border-background-200 hidden md:block">
              <img
                src={character.image}
                alt={`${character.name} 剧照`}
                title={`${character.name} - 希岛爱理角色解析`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </div>

      <section className="w-full px-4 md:px-8 pt-12">
        <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
          <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">剧情片段摘要</h2>
          <p className="text-sm md:text-base text-foreground-700 leading-loose">
            {character.plotSummary}
          </p>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <Link
            to="/characters"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-background-200 text-foreground-800 text-sm hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap w-fit"
          >
            <i className="ri-arrow-left-line" />
            返回角色解析专题
          </Link>
          <ShareButtons title={`${character.name} - 希岛爱理角色解析`} />
        </div>
      </section>
    </div>
  );
}